import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Menu,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  TextFields as TextIcon,
  Code as CodeIcon,
  DragIndicator as DragIcon,
  MoreVert as MoreIcon,
  CloudUpload as UploadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface UserAgreement {
  agreement_id: string;
  template_id: string;
  agreement_type: 'text' | 'text_with_mergetags' | 'pdf';
  agreement_title: string;
  agreement_content?: string;
  pdf_url?: string;
  pdf_filename?: string;
  order_number: number;
  is_required: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface MergeTag {
  merge_tag_id: string;
  tag_key: string;
  tag_label: string;
  tag_category: string;
  tag_description?: string;
  is_active: number;
  custom_field_id?: string;
}

interface MergeTagCategories {
  asset: MergeTag[];
  custom_field: MergeTag[];
  user: MergeTag[];
  business: MergeTag[];
  check: MergeTag[];
  date: MergeTag[];
}

interface UserAgreementsManagerProps {
  templateId: string;
  templateName: string;
  assetTypeIds?: string[];
  isDraft?: boolean;
  draftAgreements?: any[];
  onDraftAgreementsChange?: (agreements: any[]) => void;
}

const UserAgreementsManager: React.FC<UserAgreementsManagerProps> = ({
  templateId,
  templateName,
  assetTypeIds = [],
  isDraft = false,
  draftAgreements = [],
  onDraftAgreementsChange,
}) => {
  console.log('🔥🔥🔥 UserAgreementsManager LOADED - NEW CODE 🔥🔥🔥');
  console.log('Props:', { templateId, isDraft, draftAgreementsCount: draftAgreements.length, hasCallback: !!onDraftAgreementsChange });
  
  // Use draft agreements for new templates, fetch from API for existing templates
  const [agreements, setAgreements] = useState<UserAgreement[]>(isDraft ? draftAgreements : []);
  const [mergeTags, setMergeTags] = useState<MergeTagCategories>({
    asset: [],
    custom_field: [],
    user: [],
    business: [],
    check: [],
    date: [],
  });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState<UserAgreement | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [mergeTagsDialogOpen, setMergeTagsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAgreement, setSelectedAgreement] = useState<UserAgreement | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    agreement_type: 'text' as 'text' | 'text_with_mergetags' | 'pdf',
    agreement_title: '',
    agreement_content: '',
    is_required: true,
    pdf_file: null as File | null,
  });

  const fetchAgreements = useCallback(async () => {
    // Skip fetching if in draft mode
    if (isDraft) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/template-user-agreements/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgreements(response.data.data || []);
    } catch (error) {
      console.error('Error fetching user agreements:', error);
      showSnackbar('Failed to load user agreements', 'error');
    } finally {
      setLoading(false);
    }
  }, [templateId, isDraft]);

  const fetchMergeTags = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = assetTypeIds.length > 0 ? { assetTypeId: assetTypeIds[0] } : {};
      const response = await axios.get(
        `${API_BASE_URL}/template-user-agreements/merge-tags/available`,
        { headers: { Authorization: `Bearer ${token}` }, params }
      );
      setMergeTags(response.data.categories || {});
    } catch (error) {
      console.error('Error fetching merge tags:', error);
    }
  }, [assetTypeIds]);

  useEffect(() => {
    fetchAgreements();
    fetchMergeTags();
  }, [fetchAgreements, fetchMergeTags]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (agreement?: UserAgreement) => {
    if (agreement) {
      setEditingAgreement(agreement);
      setFormData({
        agreement_type: agreement.agreement_type,
        agreement_title: agreement.agreement_title,
        agreement_content: agreement.agreement_content || '',
        is_required: agreement.is_required === 1,
        pdf_file: null,
      });
    } else {
      setEditingAgreement(null);
      setFormData({
        agreement_type: 'text',
        agreement_title: '',
        agreement_content: '',
        is_required: true,
        pdf_file: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAgreement(null);
    setFormData({
      agreement_type: 'text',
      agreement_title: '',
      agreement_content: '',
      is_required: true,
      pdf_file: null,
    });
  };

  const handleSaveAgreement = async () => {
    try {
      if (!formData.agreement_title.trim()) {
        showSnackbar('Agreement title is required', 'error');
        return;
      }

      if (formData.agreement_type !== 'pdf' && !formData.agreement_content.trim()) {
        showSnackbar('Agreement content is required', 'error');
        return;
      }

      if (formData.agreement_type === 'pdf' && !formData.pdf_file && !editingAgreement) {
        showSnackbar('PDF file is required', 'error');
        return;
      }

      // DRAFT MODE: Just add to local state, don't call API
      if (isDraft) {
        console.log('🔍 DRAFT MODE: Adding agreement to draft state');
        console.log('🔍 isDraft:', isDraft);
        console.log('🔍 onDraftAgreementsChange:', !!onDraftAgreementsChange);
        
        const newAgreement: UserAgreement = {
          agreement_id: `draft_${Date.now()}`,
          template_id: 'draft',
          agreement_type: formData.agreement_type,
          agreement_title: formData.agreement_title,
          agreement_content: formData.agreement_content,
          pdf_file: formData.pdf_file,
          order_number: agreements.length,
          is_required: formData.is_required ? 1 : 0,
          is_active: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as any;
        
        const updatedAgreements = editingAgreement
          ? agreements.map(a => a.agreement_id === editingAgreement.agreement_id ? { ...a, ...newAgreement } : a)
          : [...agreements, newAgreement];
        
        console.log('🔍 Updated agreements count:', updatedAgreements.length);
        console.log('🔍 Calling setAgreements...');
        setAgreements(updatedAgreements);
        
        if (onDraftAgreementsChange) {
          console.log('🔍 Calling onDraftAgreementsChange with', updatedAgreements.length, 'agreements');
          onDraftAgreementsChange(updatedAgreements);
        } else {
          console.log('⚠️ onDraftAgreementsChange is not defined!');
        }
        
        showSnackbar(editingAgreement ? 'Agreement updated' : 'Agreement added (will be saved with template)', 'success');
        handleCloseDialog();
        return;
      }

      // NORMAL MODE: Save to API
      const token = localStorage.getItem('token');
      if (editingAgreement) {
        // Update existing agreement
        await axios.put(
          `${API_BASE_URL}/template-user-agreements/${editingAgreement.agreement_id}`,
          {
            agreement_title: formData.agreement_title,
            agreement_content: formData.agreement_content,
            is_required: formData.is_required,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar('User agreement updated successfully', 'success');
      } else {
        // Create new agreement
        if (formData.agreement_type === 'pdf' && formData.pdf_file) {
          // Upload PDF
          const formDataUpload = new FormData();
          formDataUpload.append('pdf', formData.pdf_file);
          formDataUpload.append('template_id', templateId);
          formDataUpload.append('agreement_title', formData.agreement_title);
          formDataUpload.append('order_number', String(agreements.length));
          formDataUpload.append('is_required', String(formData.is_required));

          await axios.post(
            `${API_BASE_URL}/template-user-agreements/upload-pdf`,
            formDataUpload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        } else {
          // Create text agreement
          await axios.post(
            `${API_BASE_URL}/template-user-agreements`,
            {
              template_id: templateId,
              agreement_type: formData.agreement_type,
              agreement_title: formData.agreement_title,
              agreement_content: formData.agreement_content,
              order_number: agreements.length,
              is_required: formData.is_required,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        showSnackbar('User agreement created successfully', 'success');
      }

      handleCloseDialog();
      fetchAgreements();
    } catch (error) {
      console.error('Error saving user agreement:', error);
      showSnackbar('Failed to save user agreement', 'error');
    }
  };

  const handleDeleteAgreement = async (agreementId: string) => {
    if (!window.confirm('Are you sure you want to delete this user agreement?')) {
      return;
    }

    // In draft mode, just remove from local state
    if (isDraft) {
      const updatedAgreements = agreements.filter(a => a.agreement_id !== agreementId);
      setAgreements(updatedAgreements);
      if (onDraftAgreementsChange) {
        onDraftAgreementsChange(updatedAgreements);
      }
      showSnackbar('Agreement removed', 'success');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/template-user-agreements/${agreementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('User agreement deleted successfully', 'success');
      fetchAgreements();
    } catch (error) {
      console.error('Error deleting user agreement:', error);
      showSnackbar('Failed to delete user agreement', 'error');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(agreements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setAgreements(items);

    try {
      const token = localStorage.getItem('token');
      const agreementIds = items.map(item => item.agreement_id);
      await axios.put(
        `${API_BASE_URL}/template-user-agreements/${templateId}/reorder`,
        { agreementIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error reordering agreements:', error);
      showSnackbar('Failed to reorder agreements', 'error');
      fetchAgreements(); // Revert on error
    }
  };

  const insertMergeTag = (tagKey: string) => {
    const cursorPosition = (document.getElementById('agreement-content') as HTMLTextAreaElement)?.selectionStart || 0;
    const currentContent = formData.agreement_content;
    const mergeTagText = `{{${tagKey}}}`;
    const newContent =
      currentContent.substring(0, cursorPosition) +
      mergeTagText +
      currentContent.substring(cursorPosition);
    setFormData({ ...formData, agreement_content: newContent });
  };

  const getAgreementTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon />;
      case 'text_with_mergetags':
        return <CodeIcon />;
      default:
        return <TextIcon />;
    }
  };

  const getAgreementTypeLabel = (type: string) => {
    switch (type) {
      case 'pdf':
        return 'PDF Document';
      case 'text_with_mergetags':
        return 'Text with Merge Tags';
      default:
        return 'Plain Text';
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, agreement: UserAgreement) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgreement(agreement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgreement(null);
  };

  return (
    <Box>
      {isDraft && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Adding agreements while creating the template</strong>
            <br />
            These agreements will be saved when you click "Create Template" below.
          </Typography>
        </Alert>
      )}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">User Agreements</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Agreement
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading agreements...</Typography>
            </Box>
          ) : agreements.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No user agreements configured. User agreements replace the standard loan_screen3 terms and conditions.
              Add agreements to customize the terms for this template.
            </Alert>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="agreements">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {agreements.map((agreement, index) => (
                      <Draggable
                        key={agreement.agreement_id}
                        draggableId={agreement.agreement_id}
                        index={index}
                      >
                        {(provided) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ mb: 2 }}
                          >
                            <ListItem>
                              <Box {...provided.dragHandleProps} sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                <DragIcon color="action" />
                              </Box>
                              {getAgreementTypeIcon(agreement.agreement_type)}
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle1">{agreement.agreement_title}</Typography>
                                    <Chip
                                      label={getAgreementTypeLabel(agreement.agreement_type)}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                    {agreement.is_required === 1 && (
                                      <Chip label="Required" size="small" color="error" variant="outlined" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  agreement.agreement_type === 'pdf'
                                    ? `PDF: ${agreement.pdf_filename}`
                                    : agreement.agreement_content?.substring(0, 100) + '...'
                                }
                                sx={{ ml: 2 }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  onClick={(e) => handleMenuOpen(e, agreement)}
                                >
                                  <MoreIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedAgreement) handleOpenDialog(selectedAgreement);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedAgreement) handleDeleteAgreement(selectedAgreement.agreement_id);
            handleMenuClose();
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAgreement ? 'Edit User Agreement' : 'Add User Agreement'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Agreement Title"
                value={formData.agreement_title}
                onChange={(e) => setFormData({ ...formData, agreement_title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Agreement Type</InputLabel>
                <Select
                  value={formData.agreement_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      agreement_type: e.target.value as 'text' | 'text_with_mergetags' | 'pdf',
                    })
                  }
                  disabled={!!editingAgreement}
                >
                  <MenuItem value="text">Plain Text</MenuItem>
                  <MenuItem value="text_with_mergetags">Text with Merge Tags</MenuItem>
                  <MenuItem value="pdf">PDF Document</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.agreement_type === 'pdf' ? (
              <Grid item xs={12}>
                {editingAgreement?.pdf_url ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current PDF: {editingAgreement.pdf_filename}
                    </Typography>
                    <Button
                      variant="outlined"
                      href={editingAgreement.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    Upload PDF
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pdf_file: e.target.files ? e.target.files[0] : null,
                        })
                      }
                    />
                  </Button>
                )}
                {formData.pdf_file && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {formData.pdf_file.name}
                  </Typography>
                )}
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Agreement Content
                    </Typography>
                    {formData.agreement_type === 'text_with_mergetags' && (
                      <Button
                        size="small"
                        startIcon={<InfoIcon />}
                        onClick={() => setMergeTagsDialogOpen(true)}
                      >
                        View Merge Tags
                      </Button>
                    )}
                  </Box>
                  <TextField
                    id="agreement-content"
                    fullWidth
                    multiline
                    rows={10}
                    value={formData.agreement_content}
                    onChange={(e) => setFormData({ ...formData, agreement_content: e.target.value })}
                    placeholder={
                      formData.agreement_type === 'text_with_mergetags'
                        ? 'Enter your agreement text. Use {{merge_tag}} syntax to insert dynamic values.'
                        : 'Enter your agreement text'
                    }
                    required
                  />
                </Grid>
                {formData.agreement_type === 'text_with_mergetags' && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<InfoIcon />}>
                      Use merge tags like <code>{'{{asset_name}}'}</code> or <code>{'{{user_name}}'}</code> to insert dynamic values.
                      Click "View Merge Tags" to see all available options.
                    </Alert>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                }
                label="Required (user must accept this agreement)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveAgreement} variant="contained" color="primary">
            {editingAgreement ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Tags Reference Dialog */}
      <Dialog
        open={mergeTagsDialogOpen}
        onClose={() => setMergeTagsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Available Merge Tags</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Click on a merge tag to copy it to your clipboard. You can then paste it into your agreement text.
          </Typography>
          
          {/* Show alert if custom fields are available */}
          {mergeTags.custom_field && mergeTags.custom_field.length > 0 && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>Custom Fields Available!</strong> This template has {mergeTags.custom_field.length} custom 
                field(s) that can be used as merge tags. Scroll down to see them in the "CUSTOM FIELD" section.
              </Typography>
            </Alert>
          )}
          
          {Object.entries(mergeTags).map(([category, tags]) => (
            tags.length > 0 && (
              <Box key={category} sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {category.replace('_', ' ').toUpperCase()}
                </Typography>
                <Grid container spacing={1}>
                  {tags.map((tag: MergeTag) => (
                    <Grid item key={tag.merge_tag_id}>
                      <Tooltip title={tag.tag_description || tag.tag_label}>
                        <Chip
                          label={`{{${tag.tag_key}}}`}
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${tag.tag_key}}}`);
                            insertMergeTag(tag.tag_key);
                            showSnackbar('Merge tag inserted', 'success');
                          }}
                          variant="outlined"
                          size="small"
                        />
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ mt: 2 }} />
              </Box>
            )
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeTagsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserAgreementsManager;
